import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, Sparkles, Clock, ShieldCheck } from "lucide-react";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", () => resolve(false), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Subscribe() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [businessName, setBusinessName] = useState("StenoSync");
  const [picked, setPicked] = useState("quarterly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/subscription/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/subscription/plans").then((r) => {
      setPlans(r.data?.plans || []);
      setBusinessName(r.data?.business_name || "StenoSync");
      if (r.data?.plans?.[0]) setPicked((prev) => (r.data.plans.find((p) => p.id === prev) ? prev : r.data.plans[0].id));
    }).catch(() => {});
    // Warm up the checkout script so opening the modal is instant
    loadRazorpayScript();
  }, []);

  const isExpired = status?.is_expired;
  const pickedPlan = plans.find((p) => p.id === picked);

  const pay = async () => {
    if (!pickedPlan) return;
    setLoading(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Payment gateway could not load. Check your internet and try again.");
        return;
      }
      const { data } = await api.post("/subscription/create-order", { plan_id: pickedPlan.id });
      const options = {
        key: data.key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: data.business_name || businessName,
        description: `${data.plan.label} — ${data.plan.days} days`,
        order_id: data.order_id,
        prefill: {
          name: data.user?.name || user?.name || "",
          email: data.user?.email || user?.email || "",
          contact: user?.phone || "",
        },
        notes: { plan_id: data.plan.id },
        theme: { color: "#002FA7" },
        modal: {
          ondismiss: () => setLoading(false),
        },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
              plan_id: pickedPlan.id,
            });
            toast.success("Payment successful — subscription active!");
            await refresh();
            const fresh = await api.get("/subscription/status");
            setStatus(fresh.data);
            setTimeout(() => navigate("/dashboard"), 900);
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed. Contact support if amount was debited.");
          } finally { setLoading(false); }
        },
      };
      const rz = new window.Razorpay(options);
      rz.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed. Please try again.");
        setLoading(false);
      });
      rz.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start payment. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-4">Subscription</div>
          <h1 className="text-4xl lg:text-5xl font-black tracking-tight flex items-center gap-4" data-testid="subscribe-title">
            <Sparkles className="w-10 h-10 text-brand-gold" /> Choose your plan
          </h1>
          <p className="mt-3 font-mono text-muted-foreground max-w-2xl">
            All 4 modules, unlimited attempts, certificates, streaks, class leaderboards — sab kuch shaamil hai.
          </p>
        </div>

        {/* Current status */}
        {status && (
          <div
            className={`mb-8 border-2 p-5 flex items-center justify-between flex-wrap gap-3 ${
              isExpired ? "border-brand-crimson/40 bg-red-50" : status.is_trial ? "border-yellow-500 bg-yellow-50" : "border-green-500 bg-green-50"
            }`}
            data-testid="subscribe-status"
          >
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold">Current status</div>
                <div className="font-mono text-sm">
                  {isExpired
                    ? "Trial expired — please subscribe"
                    : status.is_trial
                      ? `Free trial: ${status.days_remaining} din baaki`
                      : status.days_remaining == null
                        ? "Unlimited access"
                        : `Active subscription: ${status.days_remaining} din baaki`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="subscribe-plans">
          {plans.map((p) => {
            const active = picked === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPicked(p.id)}
                data-testid={`plan-${p.id}`}
                className={`text-left border-2 p-6 transition-all ${
                  active
                    ? "border-brand-gold/40 bg-secondary/60 -translate-y-1 shadow-[6px_6px_0_0_rgba(0,47,167,0.12)]"
                    : "border-border bg-white hover:border-brand-gold/40"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">{p.name}</div>
                  {p.badge && (
                    <span className="text-[10px] uppercase tracking-widest bg-brand-blue text-white px-2 py-0.5 font-bold">{p.badge}</span>
                  )}
                </div>
                <div className="text-3xl font-black tracking-tight mb-1">
                  ₹{p.amount_rupees.toLocaleString("en-IN")}
                  <span className="text-sm font-mono text-muted-foreground"> / {p.days} din</span>
                </div>
                <div className="text-xs font-mono text-muted-foreground">{p.tagline}</div>
                {active && <CheckCircle className="w-5 h-5 text-brand-gold mt-4" />}
              </button>
            );
          })}
        </div>

        {/* Pay CTA */}
        <div className="border-2 border-border p-6 bg-secondary/60">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-brand-gold mb-3">
            <ShieldCheck className="w-4 h-4" /> Secure payment by Razorpay
          </div>
          <p className="font-mono text-sm text-foreground/80 leading-relaxed mb-5">
            UPI, cards, netbanking aur wallets — sab supported. Payment successful hote hi aapki subscription
            <b> turant</b> activate ho jayegi.
          </p>
          <Button
            onClick={pay}
            disabled={loading || !pickedPlan}
            className="rounded-lg h-12 px-8 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
            data-testid="subscribe-pay-btn"
          >
            {loading
              ? "Opening payment…"
              : pickedPlan
                ? `Pay ₹${pickedPlan.amount_rupees.toLocaleString("en-IN")} & activate`
                : "Choose a plan"}
          </Button>
          <p className="mt-4 text-[11px] font-mono text-muted-foreground">
            Merchant: <b>{businessName}</b>. Refunds are handled case-by-case within 7 days of purchase — contact support with your Razorpay Payment ID.
          </p>
        </div>

        <div className="mt-8 text-sm font-mono text-muted-foreground">
          Wapas jaana hai?{" "}
          <Link to="/dashboard" className="text-brand-gold font-bold underline underline-offset-4">Dashboard</Link>
        </div>
      </main>
    </div>
  );
}
